/** @module Specification/SizeVariationTable */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"underscore",
	"csi",
	"csi/action/Switchboard",
	"csi/table/SlimPreferences",
	"csi/table/Widget",
	"csi/toolbar/MultiSelectSwitchboard",
	"csi/xml",
	"Specification/SizeVariationParams"
], function(declare, lang, _, csi, SwitchboardAction, SlimPreferences, Widget, MultiSelectSwitchboard, xml, SizeVariationParams) {

	return declare(Widget, {
		bResetStore: false,	// store is controlled by code below
		bSupportsQuerySettings: false,	// no sorting (or filtering)
		disableCustomViewToolbar: true,
		querySizeFixed: true, // show all results
		plugins: null, // reset default toolbar; plugins will be initialized below
		prefClass: SlimPreferences,

		/** @constant {string} ATTR_VALUE - attribute id holding value of size variation being edited */
		ATTR_VALUE: "Value",

		/** @property {string[]} bomActiveSizes - list of product size URLs selected to display; if BOM sizes are 2d, then this will contain only 1st dimension sizes */
		bomActiveSizes: null,

		/** @property {string[]} bomActiveSizes2 - list of 2nd dimension product size URLs selected to display */
		bomActiveSizes2: null,

		/** @property {Node[]} bomSizeColumns - nodes to display as matrix columns */
		bomSizeColumns: null,

		/** @property {SizeSet} bomSizeSet - set of sizes in BOM */
		bomSizeSet: null,

		/** @property {Object} bomSizeVariation - parameters for selected size variation */
		bomSizeVariation: null,

		/** @property {string} column1Name - resource to use for name of 1st column when not a 2d size set */
		column1Name: "Size1",

		/** @property {string} column2Name - resource to use for name of 2nd column when not a 2d size set */
		column2Name: "Size2",

		/** @property {string} useSecondaryToolbar - indicates whether to use secondary toolbar for size switchboards */
		useSecondaryToolbar: false,

		/** @property {Object<string,Node>} variationNodeMap - map of client nodes for matrix keyed by 1d/2d size URL */
		variationNodeMap: null,

		/** @property {boolean} _bValueChanged - indicate if edits were made */
		_bValueChanged: false,

		/** @property {boolean} displaySizeSpecs - whether or not to display size specs in table */
		displaySizeSpecs: false,

		/**
		 * Write any changes to writer 
		 * @param {Controller} ac
		 */
		onActionSizeChanges: function(ac) {
			if (this._bValueChanged) {
				if (this.bomSizeSet.has2dSizes()) {
					this.writeSizeChanges(ac, this.bomSizeVariation);
				} else {
					this.writeSizeChanges(ac, SizeVariationParams.SIZE_PARAMS, true);
					this.writeSizeChanges(ac, SizeVariationParams.QTY_PARAMS, true);
					this.writeSizeChanges(ac, SizeVariationParams.SIZE_SPEC_PARAMS, true);
				}

				this._bValueChanged = false;
			}
		},

		/**
		 * Write size changes according to variation obj
		 * @param {Controller} ac - action controller
		 * @param {Object} variationObj - contains info on which operation to be called and attribute to be hashed by
		 * @param {boolean} [filterByType] - indicates if need to filter variation map by type of variation
		 */
		writeSizeChanges: function(ac, variationObj, filterByType) {
			const {attr, nodeType, operation} = variationObj;
			let {variationNodeMap} = this;

			if (filterByType) {
				variationNodeMap = _.pick(variationNodeMap, (value) => value.$Type === nodeType);
			}

			const values = csi.hashArray(variationNodeMap, this.ATTR_VALUE, attr);

			ac.m_writer.callMethod("Specification", operation)
				.addParameter(xml.PROP_URL, xml.ATTR_REF, this.urlRoot)
				.addParameter("ValueMap", xml.ATTR_STRINGMAP, values);
		},

		/**
		 * Extension point to allow customization of view
		 */
		onResolveSizes: function(plugins) { },

		/**
		 * @override
		 * Watch the node if it's being changed, and then update the bChange flag
		 * since we can't track the change to every client node, so if the bChange set to true once, then we treat it as changed
		 */
		postAction: function(ac, bPendingAction) {
			if (ac) {
				this._bValueChanged = this._bValueChanged || (ac.newValue != ac.oldValue);
			}

			this.inherited(arguments);
		},

		/**
		 * @override
		 * Add the toolbar switchboard for the source size
		 * Initial the store for column nodes
		 */
		_resolveView: function() {
			const {bomSizeSet, useSecondaryToolbar} = this;

			if (bomSizeSet && this.getViewNode()) {
				this.plugins = [];
				this.onResolveSizes();

				if (bomSizeSet.has2dSizes()) {
					this.plugins.push(lang.mixin(new SwitchboardAction(), {
						m_id: "SelectProductSizes",
						m_name: bomSizeSet.dim1Name,
						m_postAction: "postActionRedrawNewData",
						bSecondaryToolbar: useSecondaryToolbar,
						classToolbarPlugin: MultiSelectSwitchboard,
						classToolbarPluginParams: {
							className: useSecondaryToolbar ? "csi-toolbar-btn-right" : null,
							idViewProperty: "bomActiveSizes",
							resDefault: "SelectAll",
							store: bomSizeSet.getSize1Names()
						}
					}));

					this.plugins.push(lang.mixin(new SwitchboardAction(), {
						m_id: "SelectProductSizes2",
						m_name: bomSizeSet?.dim2Name || "",
						m_postAction: "postActionRedrawNewData",
						bSecondaryToolbar: useSecondaryToolbar,
						classToolbarPlugin: MultiSelectSwitchboard,
						classToolbarPluginParams: {
							className: useSecondaryToolbar ? "csi-toolbar-btn-right" : null,
							idViewProperty: "bomActiveSizes2",
							resDefault: "SelectAll",
							store: bomSizeSet.getSize2Names()
						}
					}));
				}

				this.variationNodeMap = {};
				this._updateSizeStores();
			}

			this.inherited(arguments);
		},

		/**
		 * @override
		 * Initialize headings based on variation attribute
		 */
		onViewInitializeAttributes() {
			var defView = this.defView;
			var sizeVariation = this.bomSizeVariation;

			var nameHeading = _.extend(defView.newHeading(), {
				m_class: "csi-table-matrix-2nd-dimension",
				m_label: this.bomSizeSet?.dim2Name || this.column1Name,
				m_prop: xml.PROP_NAME,
				m_right: xml.RIGHT_NONE
			});

			if (this.bomSizeSet?.has2dSizes()) {
				const matrixHeading = this.createMatrixHeading(sizeVariation, defView);

				defView.m_headings = [nameHeading, matrixHeading];
				return;
			} else {
				const sizeMatrixHeading = this.createMatrixHeading(SizeVariationParams.SIZE_PARAMS, defView);
				const qtyMatrixHeading = this.createMatrixHeading(SizeVariationParams.QTY_PARAMS, defView);

				defView.m_headings = [nameHeading, sizeMatrixHeading, qtyMatrixHeading];

				if (this.displaySizeSpecs) {
					const sizeSpecMatrixHeading = this.createMatrixHeading(SizeVariationParams.SIZE_SPEC_PARAMS, defView);
					defView.m_headings.push(sizeSpecMatrixHeading);
				}
			}
		},

		/**
		 * Create a matrix heading for a given variation object
		 * @param {Object} variationObj - contains information on which aggregation and what attribute to map to
		 * @param {View} defView - view definition
		 * @returns 
		 */
		createMatrixHeading: function(variationObj, defView) {
			let matrixHeading = _.extend(defView.newHeading(), {
				m_name: `Matrix${variationObj.attr}`,
				m_path: xml.path(xml.PATH_OP_PARENT, variationObj.aggregation), // so model understand this is a matrix of PartMaterialSizeVariation
				matrix: {
					matrixClass: "Specification/BOM/SizeMappingMatrix"
				}
			});

			matrixHeading.addSubHeading(_.extend(defView.newHeading(), {
				m_id: variationObj.attr
			}));

			return matrixHeading;
		},

		/**
		 * Update store when sizes change
		 * @param {string[]} value
		 */
		_setBomActiveSizesAttr: function(value) {
			this._set("bomActiveSizes", value);
			this._updateSizeStores();
		},

		/**
		 * Update store when sizes change
		 * @param {string[]} value
		 */
		_setBomActiveSizes2Attr: function(value) {
			this._set("bomActiveSizes2", value);
			this._updateSizeStores();
		},

		/**
		 * In 1d case, update column name to match the name of the variation (Size/Qty/SizeSpec)
		 */
		_updateColumn2Name: function() {
			if (!this.bomSizeSet?.has2dSizes()) {
				xml.nodeSetName(this.bomSizeColumns[0], this.column2Name);
			}
		},

		/**
		 * Update row and column stores
		 */
		_updateSizeStores: function() {
			// make sure view is initialized before processing
			if (this.variationNodeMap) {
				var activeSizeSet = this.bomSizeSet.getSubSizeSet(this.bomActiveSizes, this.bomActiveSizes2);
				var b2d = activeSizeSet?.has2dSizes();
				var size1Store = activeSizeSet.getSize1Store();

				this.set("store", b2d ? activeSizeSet.getSize2Store() : size1Store);

				if (b2d) {
					this.bomSizeColumns = size1Store.query();
				} else {
					// for 1D case, make up single size node for column
					var node = this.cacheView.createClientNode(null, "ProductSize");
					this.bomSizeColumns = [node];
					this._updateColumn2Name();
				}
			}
		}
	});
});